import { registerMockAPI } from '~/mocks/mockApis';

import { useQuery } from '@tanstack/react-query';

import { axiosInstance } from '~/services';

import { API_ENDPOINT } from '~/constants';

import { receiveKeys } from './keys';
import { MOCK_RETURN_DISPOSAL_INBOUND_ADDRESS } from './useGetReturnDisposalInboundAreaQuery';

import type { TypeErrorResponseData } from '~/types/common.types';
import type { TypeGetReturnDisposalDetailResponse, TypeReturnDisposalDetailApi } from '~/types/receive.types';

/** [입고관리 > 회송/폐기 관리] 상세 조회 API */
const fetchReturnDisposalDetail = async (inspectionId: number): Promise<TypeReturnDisposalDetailApi> => {
  const { data } = await axiosInstance.get<TypeGetReturnDisposalDetailResponse>(
    `${API_ENDPOINT.RECEIVE.GET_RETURN_DISPOSAL_ISSUES}/${inspectionId}`
  );

  return data.data;
};

type TypeUseGetReturnDisposalDetailQueryParams = {
  inspectionId: number;
  enabled?: boolean;
};

/** [입고관리 > 회송/폐기 관리] 상세 조회 Query Hook (모달 오픈 시에만 enabled) */
export const useGetReturnDisposalDetailQuery = ({
  inspectionId,
  enabled = true,
}: TypeUseGetReturnDisposalDetailQueryParams) => {
  return useQuery({
    queryKey: receiveKeys.returnDisposalDetail(inspectionId),
    queryFn: () => fetchReturnDisposalDetail(inspectionId),
    enabled: enabled && !!inspectionId,
    staleTime: 0,
    gcTime: 1000 * 30,
  });
};

/** [입고관리 > 회송/폐기 관리] 상세 조회 Mock (처리방법별 시나리오 고정 응답) */
registerMockAPI<TypeGetReturnDisposalDetailResponse | TypeErrorResponseData>({
  method: 'get',
  endpoint: `${API_ENDPOINT.RECEIVE.GET_RETURN_DISPOSAL_ISSUES}/:inspectionId`,
  label: '회송폐기관리/상세 조회',
  scenarios: [
    {
      name: '회송(택배)',
      response: {
        data: {
          inspectionId: 2,
          purchaseOrderCode: 'TS2025/25-2VKB',
          issuedWarehouse: 'GPM1',
          issuedWarehouseName: '김포상온',
          goodsCode: 'HB2233445566',
          goodsName: '유리병 파손 케이스 - 프리미엄 올리브유 500ml',
          ownerCode: 'CU001',
          ownerName: '와이즐리',
          supplierCode: 'VD3737',
          supplierName: '바이클린',
          issueType: '파손',
          defectQty: 100,
          issueRegisteredAt: '2026-02-23T12:08:44',
          processMethod: 'RTS',
          processStatus: 'IN_PROGRESS',
          scrapPlannedAt: null,
          rts: {
            rtsMethod: 'PARCEL',
            rtsPlannedAt: '2026-05-09T00:00:00',
            palletReleased: false,
            inboundAddress: MOCK_RETURN_DISPOSAL_INBOUND_ADDRESS,
            parcel: {
              courierCode: 'HYUNDAI',
              courierName: '롯데택배',
              invoiceNos: ['8821-0091-2299', '8821-0091-2300', '8821-0091-2301'],
            },
            rentals: [],
          },
          scrap: null,
        },
      },
    },
    {
      name: '회송(택배) — 회수지 주소 없음',
      response: {
        data: {
          inspectionId: 2,
          purchaseOrderCode: 'TS2025/25-2VKB',
          issuedWarehouse: 'GPM1',
          issuedWarehouseName: '김포상온',
          goodsCode: 'HB2233445566',
          goodsName: '유리병 파손 케이스 - 프리미엄 올리브유 500ml',
          ownerCode: 'CU001',
          ownerName: '와이즐리',
          supplierCode: 'VD3737',
          supplierName: '바이클린',
          issueType: '파손',
          defectQty: 100,
          issueRegisteredAt: '2026-02-23T12:08:44',
          processMethod: 'RTS',
          processStatus: 'IN_PROGRESS',
          scrapPlannedAt: null,
          rts: {
            rtsMethod: 'PARCEL',
            rtsPlannedAt: '2026-05-09T00:00:00',
            palletReleased: false,
            inboundAddress: null,
            parcel: {
              courierCode: 'HYUNDAI',
              courierName: '롯데택배',
              invoiceNos: ['8821-0091-2299', '8821-0091-2300', '8821-0091-2301'],
            },
            rentals: [],
          },
          scrap: null,
        },
      },
    },
    {
      name: '회송(직배)',
      response: {
        data: {
          inspectionId: 9,
          purchaseOrderCode: 'TS2025/25-2VKB',
          issuedWarehouse: 'TP01',
          issuedWarehouseName: '평택상온',
          goodsCode: 'PD1122334455',
          goodsName: '오염 - 신선 샐러드 채소 모둠',
          ownerCode: 'CU001',
          ownerName: '와이즐리',
          supplierCode: 'VD3737',
          supplierName: '바이클린',
          issueType: '오염',
          defectQty: 24,
          issueRegisteredAt: '2026-02-23T12:08:44',
          processMethod: 'RTS',
          processStatus: 'IN_PROGRESS',
          scrapPlannedAt: null,
          rts: {
            rtsMethod: 'DIRECT',
            rtsPlannedAt: '2026-05-09T10:30:00',
            palletReleased: true,
            inboundAddress: MOCK_RETURN_DISPOSAL_INBOUND_ADDRESS,
            parcel: {
              courierCode: null,
              courierName: null,
              invoiceNos: [],
            },
            rentals: [
              {
                businessCode: 'KPP-22045',
                rentalCompanyCode: 'KPP',
                rentalCompanyName: '한국파렛트풀(KPP)',
                contactPhone: '010-4488-1102',
                contactName: '정재훈',
                releaseAddress: '경기 평택시 포승읍 평택항만길 142, 컬리 평택냉동 D-7번 도크',
              },
              {
                businessCode: 'AJU-10827',
                rentalCompanyCode: 'AJU',
                rentalCompanyName: '아주렌탈',
                contactPhone: '010-2211-3345',
                contactName: '김민지',
                releaseAddress: '서울특별시 강남구 테헤란로 456, 컬리 물류센터 B-3번 도크',
              },
            ],
          },
          scrap: null,
        },
      },
    },
    {
      // 반출 상세 주소는 최대 255자 Textarea 라, 공백 없이 이어 쓴 주소가 그대로 들어온다
      name: '장문 (반출 상세 주소)',
      response: {
        data: {
          inspectionId: 9,
          purchaseOrderCode: 'TS2025/25-2VKB',
          issuedWarehouse: 'TP01',
          issuedWarehouseName: '평택상온',
          goodsCode: 'PD1122334455',
          goodsName: '오염 - 신선 샐러드 채소 모둠',
          ownerCode: 'CU001',
          ownerName: '와이즐리',
          supplierCode: 'VD3737',
          supplierName: '바이클린',
          issueType: '오염',
          defectQty: 24,
          issueRegisteredAt: '2026-02-23T12:08:44',
          processMethod: 'RTS',
          processStatus: 'IN_PROGRESS',
          scrapPlannedAt: null,
          rts: {
            rtsMethod: 'DIRECT',
            rtsPlannedAt: '2026-05-09T10:30:00',
            palletReleased: true,
            inboundAddress: MOCK_RETURN_DISPOSAL_INBOUND_ADDRESS,
            parcel: {
              courierCode: null,
              courierName: null,
              invoiceNos: [],
            },
            rentals: [
              {
                businessCode: 'KPP-22045',
                rentalCompanyCode: 'KPP',
                rentalCompanyName: '한국파렛트풀(KPP)',
                contactPhone: '010-4488-1102',
                contactName: '정재훈',
                releaseAddress:
                  '경기도 평택시 포승읍 평택항만길 142 컬리 평택냉동물류센터 지하 1층 D-7번 도크, 반출 차량은 정문 우측 100m 직진 후 지하주차장 진입로를 이용해 주시고 차량 높이 제한 3.5m 를 확인해 주세요. 담당자 부재 시 경비실로 선연락 바랍니다.',
              },
              {
                businessCode: 'AJU-10827',
                rentalCompanyCode: 'AJU',
                rentalCompanyName: '아주렌탈',
                contactPhone: '010-2211-3345',
                contactName: '김민지',
                releaseAddress:
                  '서울특별시강남구테헤란로456컬리물류센터B3번도크지하1층반출전용게이트옆파렛트적치장우측세번째구역',
              },
            ],
          },
          scrap: null,
        },
      },
    },
    {
      name: '폐기',
      response: {
        data: {
          inspectionId: 4,
          purchaseOrderCode: 'TS2025/25-2VKB',
          issuedWarehouse: 'GPM1',
          issuedWarehouseName: '김포상온',
          goodsCode: 'HB3344556677',
          goodsName: '이물질 혼입 - 냉동 만두 1kg',
          ownerCode: 'CU001',
          ownerName: '와이즐리',
          supplierCode: 'VD3737',
          supplierName: '바이클린',
          issueType: '이물질 혼입',
          defectQty: 12,
          issueRegisteredAt: '2026-02-23T12:08:44',
          processMethod: 'SCRAP',
          processStatus: 'IN_PROGRESS',
          scrapPlannedAt: '2026-06-30T17:48:00',
          rts: null,
          scrap: { scrapFee: 18000 },
        },
      },
    },
    {
      name: '신청대기(조건부 없음)',
      response: {
        data: {
          inspectionId: 1,
          purchaseOrderCode: 'TS2025/25-2VKB',
          issuedWarehouse: 'GPM1',
          issuedWarehouseName: '김포상온',
          goodsCode: 'HB1111111115',
          goodsName: '한글표시 누락 케이스 - 견과류 스낵',
          ownerCode: 'CU001',
          ownerName: '와이즐리',
          supplierCode: 'VD3737',
          supplierName: '바이클린',
          issueType: '한글표시사항 누락',
          defectQty: 20,
          issueRegisteredAt: '2026-02-23T12:08:44',
          processMethod: 'APPLICATION_AWAITING',
          processStatus: 'PENDING_INPUT',
          scrapPlannedAt: '2026-03-10T12:08:44',
          rts: null,
          scrap: null,
        },
      },
    },
    {
      name: '서버 에러 (500)',
      response: { error: { code: 500, message: '상세 정보를 불러오지 못했습니다.' } },
      status: 500,
    },
  ],
});
